import {
  defer,
  type LinksFunction,
  type MetaFunction,
  type LoaderArgs,
  type AppLoadContext,
} from '@shopify/remix-oxygen';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useCatch,
  useLoaderData,
  useMatches,
  useNavigation,
  useLocation,
  type ShouldRevalidateFunction,
} from '@remix-run/react';
import {ShopifySalesChannel, Seo} from '@shopify/hydrogen';
import {Layout} from '~/components';
import {GenericError} from './components/GenericError';
import {NotFound} from './components/NotFound';
import {ErrorPageContent} from './components/ErrorPageContent';
import styles from './styles/app.css';
import favicon from '../public/favicon.svg';
import {seoPayload} from '~/lib/seo.server';
import {canonicalUrl, isNoIndexUrl} from '~/lib/seo';
import {languageAlternates} from '~/lib/documentSeo';
import {
  DEFAULT_LOCALE,
  getLocaleFromRequest,
  parseMenu,
  type EnhancedMenu,
} from './lib/utils';
import {HELMETKING_LOGO_URL} from './lib/const';
import invariant from 'tiny-invariant';
import {Shop, Cart} from '@shopify/hydrogen/storefront-api-types';
import {useAnalytics} from './hooks/useAnalytics';
import {ExternalScripts} from 'remix-utils';
import type {
  Customer,
  Maybe,
  Metafield,
} from '@shopify/hydrogen/storefront-api-types';
import {redirect} from '@shopify/remix-oxygen';

//components
import LoadingScreen from 'components/global/LoadingScreen';
import Module from 'components/modules/Module';
import SimplifiedChinese from 'components/global/SimplifiedChinese';

//domains
import {getLangagueCodeForSanityQuery, isTWSite} from 'domains/sanity';
import {RootQueryRes, useRootQuery as fetchRootData} from 'domains/root';
import {LRUCache} from 'lru-cache';
import {getExchangeRates, getExchangeRatesMap} from './domains/currency';
import {getCustomerMetafields} from 'domains/account';
import type {SanityModule} from 'domains/modules';
import React from 'react';

//translation
import translation from 'translations/global.json';

//data
import {currencyDetails} from '~/data/currencies';

//cookie
import {
  hmkCurrency,
  getSession,
  commitSession,
  previousLangSession,
} from './cookie.server';
import {routeLocale} from './domains/global';
import {I18nLocale} from './lib/type';

import {geolocation} from '@vercel/edge';

export const links: LinksFunction = () => {
  return [
    {rel: 'stylesheet', href: styles},
    {
      rel: 'preconnect',
      href: 'https://cdn.shopify.com',
    },
    {
      rel: 'preconnect',
      href: 'https://shop.app',
    },
    {
      rel: 'icon',
      type: 'image/svg+xml',
      href: 'https://cdn.shopify.com/s/files/1/0470/6526/8386/files/website-logo_32x32.png?v=1628839049',
    },
    {
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossOrigin: 'anonymous',
    },
    {
      rel: 'preload',
      href: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
      as: 'style',
    },
    // The stylesheet that actually applies Poppins. Without it the preload
    // above was a dangling request and the html { font-poppins } base fell
    // through to the browser's generic sans-serif — neither Poppins nor the
    // intended CJK stack ever rendered. display=swap keeps text visible while
    // the font loads; the preconnect + preload above make it fast.
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
    },
  ];
};

const scripts: any = () => {
  return [
    // {
    //   src: 'https://loox.io/widget/loox.js?shop=helmetking-0001.myshopify.com',
    //   crossOrigin: 'anonymous',
    //   async: 'true',
    // },
    // {
    //   src: 'https://www.googletagmanager.com/gtag/js?id=G-EKWP0X2TCX',
    //   crossOrigin: 'anonymous',
    //   async: 'true',
    // },
  ];
};

export const handle = {
  scripts,
  // Root data is reused on query-only navigations. Derive robots from the
  // current location so a filter change cannot leave an old index directive.
  seo: ({data, pathname, search}: any) => {
    if (!data?.seo) return {};
    const url = new URL(`${pathname}${search}`, data.seo.url).toString();
    return {
      ...data.seo,
      robots: {...data.seo.robots, noIndex: isNoIndexUrl(url)},
    };
  },
};

export const meta: MetaFunction = () => ({
  charset: 'utf-8',
  viewport: 'width=device-width,initial-scale=1',
});

const cache = new LRUCache<string, RootQueryRes>({
  max: 500,
  ttl: 1000 * 60 * 60 * 24,
});

/**
 * Root data (menus, settings, locale/currency session) never depends on the
 * query string, but Remix revalidates every loader on any navigation — so a
 * PLP filter/sort/page click was re-running this heavy loader and making the
 * whole page feel like a reload. Skip it on search-only navigations;
 * pathname changes, form submissions and explicit revalidation (currency
 * switcher) still refresh it.
 */
export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  formMethod,
  defaultShouldRevalidate,
}) => {
  if (
    formMethod == null &&
    currentUrl.pathname === nextUrl.pathname &&
    currentUrl.search !== nextUrl.search
  ) {
    return false;
  }
  return defaultShouldRevalidate;
};

/**
 * The document language as a BCP 47 tag. `locale.language` is Shopify's
 * LanguageCode enum (ZH_TW, EN) and was written straight into <html lang>,
 * which no browser, screen reader or search engine parses as a language.
 * The enum stays untouched — 60-odd comparisons depend on it — only the
 * attribute is translated. /zh-cn keeps language ZH_TW for data lookups but
 * is Simplified to the reader, and SimplifiedChinese.tsx already sets
 * zh-Hans on the client; saying so from the server keeps the two in step.
 */
function htmlLang(locale: I18nLocale): string {
  if (locale.pathPrefix === '/zh-cn') return 'zh-Hans';
  if (locale.language === 'EN') return `en-${locale.country}`;
  return `zh-Hant-${locale.country}`;
}

export async function loader({request, context, params}: LoaderArgs) {
  const req_url = new URL(request.url);
  const req_url_host = req_url.host;

  // The storefront only implements '' (zh-TW), '/en' and '/zh-cn'. Anything
  // else in the locale position is a prefix we do not serve, and it used to
  // render a full 200 copy of the page underneath it:
  //
  //   /anything/products/airohgp550svektor -> 200, canonical to itself
  //   /foo/collections/bestsellers         -> 200, canonical to itself
  //
  // The guard only fired for segments *shaped* like a locale, so /zz
  // redirected while /anything did not, and canonicalUrl rewrites the host
  // but never the path — so every junk prefix minted an indexable,
  // self-canonical duplicate of the whole storefront, and its pagination
  // links carried the prefix onward. Unbounded in the number of prefixes.
  //
  // Remix only captures `lang` when the segments after it match a real
  // route, so a captured segment that is not a locale always has a real page
  // underneath: strip it and redirect there. The single-segment case is the
  // exception — ($lang)/index.tsx captures retired root slugs as `lang` and
  // salvages them (legacyRedirectTarget, salvageLegacySlug), so it keeps the
  // narrower locale-shaped test and everything else falls through to it.
  //
  // A purely numeric segment is never a junk prefix — it is the shop id in
  // Shopify's account-activation URL, /47065268386/orders/<token>/authenticate,
  // which 302s to the checkout domain. That path matches its route with
  // `lang` unset, so this guard should not see it either way; the carve-out
  // is here so a routing change can never turn a customer's activation link
  // into a redirect to a 404.
  const numericShopId = /^\d+$/.test(params.lang ?? '');
  if (
    params.lang &&
    !numericShopId &&
    getLocaleFromRequest(request).pathPrefix === ''
  ) {
    const rest = req_url.pathname.slice(params.lang.length + 1);
    const hasFurtherSegment = rest.replace(/^\/+/, '') !== '';
    if (hasFurtherSegment || /^[a-z]{2}(-[a-z]{2,4})?$/i.test(params.lang)) {
      return redirect((rest || '/') + req_url.search, 301);
    }
  }

  //get cookie session
  const cookieHeader = request.headers.get('Cookie');
  const session = await getSession(cookieHeader);
  const currencyFromSession = await session.get('hmk-currency');
  const currencyTWFromSession = await session.get('hmk-tw-currency');

  const isTWStorefront = context.storefront.i18n.country === 'TW';
  const selectedCurrency = isTWStorefront
    ? currencyTWFromSession ?? 'TWD'
    : currencyFromSession ?? 'HKD';

  /**
   * Lazily commit the session: only emit `Set-Cookie` when it would actually
   * change client state. Attaching it on every response defeats CDN caching
   * and hands session cookies to crawlers/anonymous visitors that are in the
   * default state anyway.
   *
   * - No session cookie + default currency for the host -> no cookie at
   *   all. The language is not part of the state: it is in the URL, and
   *   nothing reads previous-lang any more. Writing it made every /en
   *   response carry Set-Cookie, so no /en page was ever edge-cached.
   * - Session cookie present and it already stores exactly what we'd store
   *   -> skip the commit.
   * - Otherwise -> commit and attach `Set-Cookie` (same values as before).
   */
  const hasSessionCookie = /(?:^|;\s*)hmk-currency=/.test(cookieHeader ?? '');
  const storedCurrency = isTWStorefront
    ? currencyTWFromSession
    : currencyFromSession;
  const isDefaultState = selectedCurrency === (isTWStorefront ? 'TWD' : 'HKD');
  const isAlreadyStored = storedCurrency === selectedCurrency;

  let setCookie: string | undefined;
  if (hasSessionCookie ? !isAlreadyStored : !isDefaultState) {
    if (isTWStorefront) {
      session.set('hmk-tw-currency', selectedCurrency);
    } else {
      session.set('hmk-currency', selectedCurrency);
    }
    setCookie = await commitSession(session);
  }

  // NOTE: the TW <-> www geo redirect that used to live here has moved to
  // the Vercel Edge Middleware (/middleware.ts) so it is decided before the
  // request ever reaches the origin. `requestingFrom` is still computed for
  // rootData announcement targeting (`ipAddressLocation`).
  const geo = geolocation(request);

  const requestingFrom = geo.country ?? 'HK';

  //Sanity
  const languageCode = getLangagueCodeForSanityQuery(
    context.storefront.i18n.language,
  );
  let rootData: RootQueryRes;

  // Key the cache by language so a zh_TW render is never served to an /en
  // visitor (or vice versa) — the previous single 'rootData' key leaked
  // whichever language populated it first. The key makes the old
  // `is_lang_changed` cache-bypass unnecessary.
  const rootDataCacheKey = `rootData:${languageCode}`;
  const cachedRootData = cache.get(rootDataCacheKey);
  if (cachedRootData) {
    rootData = cachedRootData;
  } else {
    rootData = await fetchRootData({
      ipAddressLocation: geo.country,
      languageCode,
    });
    cache.set(rootDataCacheKey, rootData);
  }

  const [customerAccessToken, cartId, layout] = await Promise.all([
    context.session.get('customerAccessToken'),
    context.session.get('cartId'),
    getLayoutData(context),
  ]);

  let customer;
  let metafields;

  if (customerAccessToken) {
    try {
      customer = await getCustomer(context, customerAccessToken);
      if (customer) {
        const [customerWithMetafields, metafieldsError] =
          await getCustomerMetafields({customerId: customer.id});
        metafields = (customerWithMetafields as any)?.edges as Maybe<
          {node: Metafield}[]
        >;
      }
    } catch (error) {
      console.log(
        'Invalid customer access token, clearing from session:',
        error,
      );
      // Clear the invalid token from session
      context.session.unset('customerAccessToken');
      // Set the cookie to persist the session change
      await context.session.commit();
    }
  }

  const seo = seoPayload.root({
    shop: layout.shop,
    // Canonical = clean host+path (query/hash stripped) so filtered, sorted,
    // paginated and tracking-tagged URLs all consolidate onto one canonical.
    url: canonicalUrl(request.url),
    title: rootData.seo.title,
    description: rootData.seo.description,
  });

  // Match the `X-Robots-Tag` set in entry.server.tsx. Shipping `index,follow`
  // in the meta tag while the header says noindex is a contradiction Google
  // resolves by taking the restrictive side anyway — better not to send it.
  if (isNoIndexUrl(request.url)) {
    seo.robots = {noIndex: true, noFollow: false};
  }

  const countryCode = isTWSite(request.url) ? 'TW' : 'HK';
  const baseCurrency =
    rootData.currencySettings.base_currencies
      .find((c: any) => c.country === countryCode)
      ?.currency?.split('-')[1] ?? 'HKD';

  /**
   * A shopper whose cookie holds a currency the shop has since stopped
   * offering would otherwise sit on a dead selection: no rate exists for it,
   * so every price falls back to its own currency while the selector still
   * shows the retired code. Fall back to the storefront's default, and let
   * the stale cookie be overwritten the next time they pick one.
   */
  const offeredCurrencies = new Set(
    (rootData.currencySettings.all_currencies ?? [])
      .map((c) => c?.currency?.split('-')[1])
      .filter(Boolean),
  );
  const resolvedCurrency = offeredCurrencies.has(selectedCurrency)
    ? selectedCurrency
    : isTWStorefront
    ? 'TWD'
    : 'HKD';

  const exchangeRates = getExchangeRates(
    rootData.currencySettings,
    baseCurrency,
  );
  const hkExchangeRates = getExchangeRates(rootData.currencySettings, 'HKD');
  const twExchangeRates = getExchangeRates(rootData.currencySettings, 'TWD');
  const exchangeRatesMap = getExchangeRatesMap(exchangeRates);
  const hkExchangeRatesMap = getExchangeRatesMap(hkExchangeRates);
  const twExchangeRatesMap = getExchangeRatesMap(twExchangeRates);

  const wishlist =
    metafields && metafields.find((m) => m.node.key === 'wishlist')?.node;
  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  const RECAPTCHA_KEY =
    process.env.NODE_ENV === 'development'
      ? '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
      : process.env.RECAPTCHA_KEY;

  return defer(
    {
      // googleReviews,
      // facebookReviews,
      isLoggedIn: Boolean(customerAccessToken),
      layout,
      productSettings: rootData.productSettings,
      wishlist,
      translation: translation[selectedLocale],
      currency: {
        exchangeRates: exchangeRatesMap,
        hkExchangeRates: hkExchangeRatesMap,
        twExchangeRates: twExchangeRatesMap,
        baseCurrency,
        selectedCurrency: resolvedCurrency,
        all_currencies: rootData.currencySettings.all_currencies.map((c) => {
          const currency = c.currency.split('-')[1];
          return {
            ...c,
            currency,
            exchange_rates: c.exchange_rates.map((er) => ({
              ...er,
              base_currency: er.base_currency.split('-')[1],
            })),
            details: currencyDetails.find(
              (currencyDetail) =>
                currencyDetail.country === c.currency.split('-')[0],
            ),
          };
        }),
      },
      selectedLocale: context.storefront.i18n,
      cart: cartId ? getCart(context, cartId, request.url) : undefined,
      analytics: {
        shopifySalesChannel: ShopifySalesChannel.hydrogen,
        shopId: layout.shop.id,
      },
      seo,
      logo: rootData.logo,
      announcement: rootData.announcement,
      latestBlogs: rootData.latestBlogs,
      footer: rootData.footer,
      menu: selectedLocale === 'en' ? rootData.menu_en : rootData.menu_zh,
      featuredBrands: rootData.featuredBrands,
      allBrands: rootData.allBrands,
      sidebar: rootData.sidebarItems,
      business: rootData.business,
      emailSignUp: rootData.emailSignUp,
      notFoundPage: rootData.notFoundPage,
      categories: rootData.categories,
      req_url_host,
      ENV: {
        RECAPTCHA_KEY: '6LeoLxgpAAAAAE5Z-HyvQV4_DI0SNz6F0q6CMGLu',
        // Microsoft Clarity heatmaps/session recordings — the script only
        // renders when the project id is configured in Vercel.
        CLARITY_PROJECT_ID: process.env.CLARITY_PROJECT_ID ?? '',
      },
      ipAddressLocation: requestingFrom,
      // gaTrackingId: process.env.GA_TRACKING_ID ?? '',
    },
    {
      // Only attach Set-Cookie when the session actually changed (see the
      // lazy commit above) — cookie-less default responses stay cacheable.
      headers: setCookie ? {'Set-Cookie': setCookie} : {},
    },
  );
}

export default function App() {
  const data = useLoaderData<typeof loader>();
  const locale = data.selectedLocale ?? DEFAULT_LOCALE;
  const hasUserConsent = true;

  useAnalytics(hasUserConsent, locale);
  const location = useLocation();

  const navigation = useNavigation();

  //   React.useEffect(()=>{
  // if(typeof window !== 'undefined'){
  //   require('../public/looxReviews')
  // }
  //   }, [])

  // Third-party tags that write into the document must not run before
  // hydration.
  // React hydrates the whole document; Clarity's loader inserted its <script>
  // ahead of the first script in <head>, and gtag.js injected the Ads and
  // doubleclick tags there as soon as it read the queued config — both before
  // React got to compare. React then found nodes the server never sent
  // (error #418, then #423) and re-rendered every page from scratch on the
  // client, which is what set LCP. An effect runs after hydration, so the
  // same tags load without touching what React checks. The inline gtag stub
  // above keeps queueing events meanwhile; only a visitor who leaves before
  // hydration goes uncounted.
  const clarityProjectId = data.ENV?.CLARITY_PROJECT_ID;
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') return;
    const load = (id: string, src: string) => {
      if (document.getElementById(id)) return;
      const script = document.createElement('script');
      script.id = id;
      script.async = true;
      script.src = src;
      document.head.appendChild(script);
    };
    load('gtag-js', 'https://www.googletagmanager.com/gtag/js?id=G-EKWP0X2TCX');
    // PreProduct (pre-orders) was an async <script> in <head>. Whenever it
    // finished before React hydrated, it wrote into <body> first and hydration
    // failed the same way — intermittently, which is why it hid behind the
    // other two tags. It scans the page for its pledge widget, so running it
    // against the hydrated DOM is what it needs anyway: anything it inserted
    // before hydration was thrown away with the rest of the server HTML.
    load(
      'preproduct-js',
      'https://preproduct.onrender.com/preproduct-embed.js',
    );
    if (clarityProjectId) {
      const w = window as unknown as {
        clarity?: ((...args: unknown[]) => void) & {q?: unknown[]};
      };
      w.clarity =
        w.clarity ||
        Object.assign(
          (...args: unknown[]) => {
            (w.clarity!.q = w.clarity!.q || []).push(args);
          },
          {q: [] as unknown[]},
        );
      load('clarity-tag', `https://www.clarity.ms/tag/${clarityProjectId}`);
    }
  }, [clarityProjectId]);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag && location) {
      window.gtag('config', 'G-EKWP0X2TCX', {
        page_path: location.pathname,
      });
    }
  }, [location]);

  React.useEffect(() => {
    if (data.req_url_host === 'en.helmetking.com') {
      window.location.href = 'https://www.helmetking.com/en';
    }

    //redirect hk domain
    if (
      data.req_url_host === 'hk.helmetking.com' ||
      data.req_url_host === 'eshop.helmetking.com'
    ) {
      window.location.href = 'https://www.helmetking.com';
    }
  }, [data.req_url_host]);

  // The bar now always renders when there's a latest blog post (it shows that
  // title), so the sticky-offset class must account for that too — not just
  // the CMS announcement being enabled.
  const hasAnnouncement = Boolean(
    (data.latestBlogs as {blog_title?: string}[] | undefined)?.some(
      (b) => b?.blog_title,
    ) || data.announcement?.enabled,
  );

  // Hydrogen's <Seo> only emits og:image:url (+type/width/height), but
  // WhatsApp and some parsers only read the plain og:image property.
  // Mirror the deepest route's seo media (product/collection image) with
  // the site logo as fallback.
  const matches = useMatches();
  const routeSeoMedia = matches
    .map((m) => (m.data as any)?.seo?.media)
    .filter(Boolean)
    .pop();
  const ogImage =
    (typeof routeSeoMedia === 'string' ? routeSeoMedia : routeSeoMedia?.url) ??
    HELMETKING_LOGO_URL;
  // The URL <Seo> renders as canonical: the deepest route's seo.url, else the
  // root's query-less page URL.
  const canonicalSeoUrl = matches
    .map((m) => (m.data as any)?.seo?.url)
    .filter((u): u is string => typeof u === 'string' && u.length > 0)
    .pop();
  const rootSeoUrl =
    ('seo' in data ? data.seo.url : undefined) ||
    canonicalSeoUrl ||
    'https://www.helmetking.com/';
  const requestSeoUrl = new URL(
    `${location.pathname}${location.search}`,
    rootSeoUrl,
  ).toString();
  const alternates = languageAlternates(
    canonicalSeoUrl || rootSeoUrl,
    requestSeoUrl,
    matches.slice(1).some((match) => {
      const route = match.data as any;
      return (
        route?.seo?.robots?.noIndex ||
        route?.hasEnglishTranslation === false ||
        route?.hasTraditionalTranslation === false
      );
    }),
  );

  return (
    <html lang={htmlLang(locale)}>
      <head>
        <link
          rel="manifest"
          href="https://progressier.app/sVyULH7c5lWxwMfq2xv7/progressier.json"
        />
        <script
          defer
          src="https://progressier.app/sVyULH7c5lWxwMfq2xv7/script.js"
        ></script>
        <Seo />
        <meta
          property="og:locale"
          content={
            locale.pathPrefix === '/zh-cn'
              ? 'zh_CN'
              : `${locale.language === 'EN' ? 'en' : 'zh'}_${locale.country}`
          }
        />
        <meta property="og:image" content={ogImage} />
        {/* Twitter/X Card: Hydrogen's <Seo> emits og:* but no twitter:*.
            twitter:title/description fall back to og:* when omitted; card +
            image are what X needs to render a large preview. */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogImage} />
        {alternates.map((alternate) => (
          <link rel="alternate" key={alternate.hrefLang} {...alternate} />
        ))}
        <Meta />
        <Links />
        {/* Bundler script temporarily disabled due to CORS issues */}
        {/* <script
          async
          dangerouslySetInnerHTML={{
            __html: `(function() {function asyncLoad() {var urls = ["https://cdn-bundler.nice-team.net/app/js/bundler.js?shop=helmetking-0001.myshopify.com"];for (var i = 0; i < urls.length; i++) {var s = document.createElement('script');s.type = 'text/javascript';s.async = true;s.src = urls[i];var x = document.getElementsByTagName('script')[0];x.parentNode.insertBefore(s, x);}};if(window.attachEvent) {window.attachEvent('onload', asyncLoad);} else {window.addEventListener('load', asyncLoad, false);}})();`,
          }}
        /> */}
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(data.ENV).replace(
              /</g,
              '\\u003c',
            )}`,
          }}
        />
      </head>
      <body className={hasAnnouncement ? 'has-announcement' : ''}>
        {process.env.NODE_ENV === 'development' ? null : (
          <>
            {/* GA4 / Google Ads: only the dataLayer stub is inline, so calls
                made before gtag.js arrives are queued, not lost. gtag.js
                itself and Clarity load after hydration — see the effect in
                App. */}
            <script
              async
              id="gtag-init"
              dangerouslySetInnerHTML={{
                __html: `
        console.log('gtag');
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());

        // GA4 config
        gtag('config', 'G-EKWP0X2TCX', {
          page_path: window.location.pathname,
        });

        // Google Ads config
        gtag('config', 'AW-16976916805');
      `,
              }}
            />
          </>
        )}
        <Layout
          layout={data.layout as LayoutData}
          key={`${locale.language}-${locale.country}`}
        >
          {/* Full-page splash only when actually changing page — same-page
              navigations (PLP filter/sort/pagination just change the query
              string) update in place, so covering them with the splash made
              every checkbox click look like a full reload. */}
          {navigation.state !== 'idle' &&
          navigation.location?.pathname !== location.pathname ? (
            <LoadingScreen logo={data.logo.headerLogo} />
          ) : null}
          <Outlet />
        </Layout>
        <SimplifiedChinese />
        <ScrollRestoration />
        <ExternalScripts />
        <Scripts />
      </body>
    </html>
  );
}

export function CatchBoundary() {
  const [root] = useMatches();
  const caught = useCatch();
  const isNotFound = caught.status === 404;
  const locale = (root?.data?.selectedLocale ?? DEFAULT_LOCALE) as I18nLocale;
  const notFoundPage = root?.data?.notFoundPage as {
    page_sections_en: SanityModule[] | null;
    page_sections: SanityModule[] | null;
  };
  const pageSections =
    locale.language === 'ZH_TW'
      ? notFoundPage?.page_sections
      : notFoundPage?.page_sections_en;

  return (
    <html lang={htmlLang(locale)}>
      <head>
        <title>{isNotFound ? 'Not found' : 'Error'}</title>
        <meta name="robots" content="noindex, follow" />
        <Meta />
        <Links />
      </head>
      <body>
        {root?.data?.layout ? (
          <Layout
            layout={root.data.layout}
            key={`${locale.language}-${locale.country}`}
          >
            <ErrorPageContent
              isNotFound={isNotFound}
              isEnglish={locale.language === 'EN'}
              homeUrl={`${locale.pathPrefix || ''}/`}
            />
            <div className="py-10">
              {pageSections?.map((module, index) => (
                <Module key={module._key} module={module} />
              ))}
            </div>
          </Layout>
        ) : (
          <ErrorPageContent
            isNotFound={isNotFound}
            isEnglish={locale.language === 'EN'}
            homeUrl={`${locale.pathPrefix || ''}/`}
          />
        )}
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary({error}: {error: Error}) {
  const [root] = useMatches();
  const locale = (root?.data?.selectedLocale ?? DEFAULT_LOCALE) as I18nLocale;
  const notFoundPage = root?.data?.notFoundPage as {
    page_sections_en: SanityModule[] | null;
    page_sections: SanityModule[] | null;
  };
  const pageSections =
    locale.language === 'ZH_TW'
      ? notFoundPage?.page_sections
      : notFoundPage?.page_sections_en;

  return (
    <html lang={htmlLang(locale)}>
      <head>
        <title>Error</title>
        <meta name="robots" content="noindex, follow" />
        <Meta />
        <Links />
      </head>
      <body>
        <ErrorPageContent
          isEnglish={locale.language === 'EN'}
          homeUrl={`${locale.pathPrefix || ''}/`}
        />
        <div className="py-10">
          {pageSections?.map((module, index) => (
            <Module key={module._key} module={module} />
          ))}
        </div>
        {/* <Layout layout={root?.data?.layout}>
          Error
          <GenericError error={error} />
        </Layout> */}
        <Scripts />
      </body>
    </html>
  );
}

const LAYOUT_QUERY = `#graphql
  query layoutMenus(
    $language: LanguageCode
    $headerMenuHandle: String!
    $footerMenuHandle: String!
  ) @inContext(language: $language) {
    shop {
      id
      name
      description
      primaryDomain {
        url
      }
      brand {
       logo {
         image {
          url
         }
       }
       coverImage {
         image {
          url
         }
       }
     }
    }
    headerMenu: menu(handle: $headerMenuHandle) {
      id
      items {
        ...MenuItem
        items {
          ...MenuItem
        }
      }
    }
    footerMenu: menu(handle: $footerMenuHandle) {
      id
      items {
        ...MenuItem
        items {
          ...MenuItem
        }
      }
    }
  }
  fragment MenuItem on MenuItem {
    id
    resourceId
    tags
    title
    type
    url
  }
`;

export interface LayoutData {
  headerMenu: EnhancedMenu;
  footerMenu: EnhancedMenu;
  shop: Shop;
  cart?: Promise<Cart>;
}

async function getLayoutData({storefront}: AppLoadContext) {
  const HEADER_MENU_HANDLE = 'main-menu';
  const FOOTER_MENU_HANDLE = 'footer';

  const data = await storefront.query<LayoutData>(LAYOUT_QUERY, {
    variables: {
      headerMenuHandle: HEADER_MENU_HANDLE,
      footerMenuHandle: FOOTER_MENU_HANDLE,
      language: storefront.i18n.language,
    },
    // Shop-wide layout data (shop info + menus): safe to cache long.
    // The cache key includes the query variables, so languages don't mix.
    cache: storefront.CacheLong(),
  });

  invariant(data, 'No data returned from Shopify API');

  /*
    Modify specific links/routes (optional)
    @see: https://shopify.dev/api/storefront/unstable/enums/MenuItemType
    e.g here we map:
      - /blogs/news -> /news
      - /blog/news/blog-post -> /news/blog-post
      - /collections/all -> /products
  */
  const customPrefixes = {BLOG: '', CATALOG: 'products'};

  const headerMenu = data?.headerMenu
    ? parseMenu(data.headerMenu, customPrefixes)
    : undefined;

  const footerMenu = data?.footerMenu
    ? parseMenu(data.footerMenu, customPrefixes)
    : undefined;

  return {shop: data.shop, headerMenu, footerMenu};
}

const CART_QUERY = `#graphql
  query CartQuery($cartId: ID!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
    cart(id: $cartId) {
      ...CartFragment
    }
  }

  fragment CartFragment on Cart {
    id
    checkoutUrl
    totalQuantity
    buyerIdentity {
      countryCode
      customer {
        id
        email
        firstName
        lastName
        displayName
      }
      email
      phone
    }
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          attributes {
            key
            value
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            amountPerQuantity {
              amount
              currencyCode
            }
            compareAtAmountPerQuantity {
              amount
              currencyCode
            }
          }
          merchandise {
            ... on ProductVariant {
              id
              availableForSale
              compareAtPrice {
                ...MoneyFragment
              }
              price {
                ...MoneyFragment
              }
              requiresShipping
              title
              image {
                ...ImageFragment
              }
              product {
                handle
                title
                id
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
    cost {
      subtotalAmount {
        ...MoneyFragment
      }
      totalAmount {
        ...MoneyFragment
      }
      totalDutyAmount {
        ...MoneyFragment
      }
      totalTaxAmount {
        ...MoneyFragment
      }
    }
    note
    attributes {
      key
      value
    }
    discountCodes {
      code
    }
  }

  fragment MoneyFragment on MoneyV2 {
    currencyCode
    amount
  }

  fragment ImageFragment on Image {
    id
    url
    altText
    width
    height
  }
`;

export async function getCart(
  {storefront}: AppLoadContext,
  cartId: string,
  url: string,
) {
  invariant(storefront, 'missing storefront client in cart query');

  const {cart} = await storefront.query<{cart?: Cart}>(CART_QUERY, {
    variables: {
      cartId,
      country: isTWSite(url) ? 'TW' : 'HK',
      language: storefront.i18n.language,
    },
    cache: storefront.CacheNone(),
  });

  return cart;
}

export async function getCustomer(
  context: AppLoadContext,
  customerAccessToken: string,
) {
  const {storefront} = context;

  const data = await storefront.query<{
    customer: Customer;
  }>(CUSTOMER_QUERY, {
    variables: {
      customerAccessToken,
      country: context.storefront.i18n.country,
      language: context.storefront.i18n.language,
    },
  });

  /**
   * If the customer failed to load, we assume their access token is invalid.
   */
  if (!data || !data.customer) {
    return undefined;
  }

  return data.customer;
}

const CUSTOMER_QUERY = `#graphql
  query CustomerDetails(
    $customerAccessToken: String!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    customer(customerAccessToken: $customerAccessToken) {
      id
      firstName
      lastName
      phone
      email
      defaultAddress {
        id
        formatted
        firstName
        lastName
        company
        address1
        address2
        country
        province
        city
        zip
        phone
      }
      addresses(first: 6) {
        edges {
          node {
            id
            formatted
            firstName
            lastName
            company
            address1
            address2
            country
            province
            city
            zip
            phone
          }
        }
      }
      orders(first: 250, sortKey: PROCESSED_AT, reverse: true) {
        edges {
          node {
            id
            orderNumber
            processedAt
            financialStatus
            fulfillmentStatus
            currentTotalPrice {
              amount
              currencyCode
            }
            lineItems(first: 2) {
              edges {
                node {
                  variant {
                    image {
                      url
                      altText
                      height
                      width
                    }
                  }
                  title
                }
              }
            }
          }
        }
      }
    }
  }
`;
